import { Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, TextField, InputAdornment, Grid } from '@mui/material';
import { Add, Print, PictureAsPdf, Send, Search, Visibility, Cancel } from '@mui/icons-material';
import { facturasCF } from '../billingData';

const ConsumerFinal = () => {
  const getStatusColor = (estado) => {
    switch (estado) {
      case 'emitida': return 'success';
      case 'anulada': return 'error';
      case 'pendiente': return 'warning';
      default: return 'default';
    }
  };

  const totalMonto = facturasCF.reduce((acc, doc) => acc + doc.monto, 0);
  const documentosEmitidos = facturasCF.filter(doc => doc.estado === 'emitida').length;
  const documentosAnulados = facturasCF.filter(doc => doc.estado === 'anulada').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            Facturas de Consumidor Final
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Documentos emitidos a consumidores finales (sin NIT)
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} color="primary">
          Nueva Factura CF
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Documentos</Typography>
              <Typography variant="h4" color="primary">{facturasCF.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Emitidos</Typography>
              <Typography variant="h4" color="success.main">{documentosEmitidos}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#ffebee' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Anulados</Typography>
              <Typography variant="h4" color="error.main">{documentosAnulados}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por cliente, número de documento..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>NIT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Monto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturasCF.map((doc) => (
                  <TableRow 
                    key={doc.id}
                    sx={{ 
                      opacity: doc.estado === 'anulada' ? 0.6 : 1,
                      '&:hover': { backgroundColor: '#fafafa' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.serie}-{doc.correlativo}
                      </Typography>
                    </TableCell>
                    <TableCell>{doc.fecha}</TableCell>
                    <TableCell>{doc.cliente}</TableCell>
                    <TableCell>{doc.nit}</TableCell>
                    <TableCell align="right">${doc.total.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={doc.estado} 
                        color={getStatusColor(doc.estado)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" title="Ver">
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Imprimir" color="primary">
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="PDF" color="error">
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                      {doc.estado === 'emitida' && (
                        <IconButton size="small" title="Anular" color="warning">
                          <Cancel fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Info */}
      <Card sx={{ mt: 3, backgroundColor: '#e3f2fd' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Información
          </Typography>
          <Typography variant="body2">
            Las facturas de consumidor final (CF) se emiten a clientes que no requieren deducción de impuestos.
            No generan crédito fiscal para el comprador.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConsumerFinal;
